import React from 'react';

const SectionHeader = ({ num, tag, title, color = '#F97316' }) => (
  <div style={{ marginBottom: '36px', position: 'relative' }}>
    <div
      style={{
        fontFamily: 'Impact, sans-serif',
        fontSize: 'clamp(4rem, 10vw, 8rem)',
        fontWeight: 900,
        color: `${color}08`,
        lineHeight: 1,
        position: 'absolute',
        top: '-20px',
        left: '-10px',
        userSelect: 'none',
        letterSpacing: '-0.04em'
      }}
    >
      {num}
    </div>
    <div style={{ position: 'relative', zIndex: 1 }}>
      <div
        style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.6875rem',
          color: color,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginBottom: '8px'
        }}
      >
        {tag}
      </div>
      <h2
        style={{
          fontFamily: 'Impact, sans-serif',
          fontSize: 'clamp(1.5rem, 3.5vw, 2.75rem)',
          fontWeight: 900,
          color: '#EEF0F2',
          textTransform: 'uppercase',
          letterSpacing: '-0.02em',
          lineHeight: 1
        }}
      >
        {title}
      </h2>
    </div>
  </div>
);

const BodyText = ({ children }) => (
  <p
    style={{
      fontFamily: 'Georgia, serif',
      fontSize: '1rem',
      color: 'rgba(238,240,242,0.72)',
      lineHeight: 1.85,
      marginBottom: '20px',
      maxWidth: '760px'
    }}
  >
    {children}
  </p>
);

const Divider = ({ color = '#F97316' }) => (
  <div style={{ margin: '48px 0', display: 'flex', alignItems: 'center', gap: '16px' }}>
    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
    <div style={{ width: '8px', height: '8px', background: color, transform: 'rotate(45deg)' }} />
    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
  </div>
);

const AccountSubscriptionSection = () => {
  return (
    <>
      {/* Section 3: User Account */}
      <section id="konto" style={{ marginBottom: '80px' }}>
        <SectionHeader
          num="03"
          tag="§ 3 — BRUKERKONTO & ANSVAR"
          title="Brukerkonto & brukeransvar"
          color="#10B981"
        />

        <BodyText>
          For å bruke NordPass sine betalingstjenester må du opprette en brukerkonto. Du er ansvarlig for å oppgi nøyaktig og fullstendig informasjon ved registrering, og for å holde denne informasjonen oppdatert.
        </BodyText>

        <div
          style={{
            background: 'rgba(16,185,129,0.06)',
            border: '1px solid rgba(16,185,129,0.2)',
            padding: '28px',
            marginBottom: '28px'
          }}
        >
          <div
            style={{
              fontFamily: 'Syne, sans-serif',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#10B981',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginBottom: '16px'
            }}
          >
            Brukerforpliktelser
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              'Du er ansvarlig for all aktivitet som skjer under din konto',
              'Du må holde ditt passord konfidensielt og ikke dele det med andre',
              'Du må umiddelbart varsle NordPass om uautorisert bruk av din konto',
              'Du må være minst 18 år gammel, eller ha foreldres/verges samtykke',
              'Du kan ikke opprette mer enn én konto per person uten skriftlig tillatelse',
              'Du er ansvarlig for å sikre at din bruk av tjenestene er i samsvar med gjeldende norsk lov',
            ]?.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div
                  style={{
                    width: '6px',
                    height: '6px',
                    background: '#10B981',
                    borderRadius: '50%',
                    marginTop: '7px',
                    flexShrink: 0
                  }}
                />
                <span
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.875rem',
                    color: 'rgba(238,240,242,0.72)',
                    lineHeight: 1.6
                  }}
                >
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>

        <BodyText>
          NordPass forbeholder seg retten til å suspendere eller slette kontoer som misbrukes, inneholder falsk informasjon, eller brukes i strid med disse vilkårene.
        </BodyText>
      </section>

      <Divider color="#10B981" />

      {/* Section 4: Subscription Terms */}
      <section id="abonnement" style={{ marginBottom: '80px' }}>
        <SectionHeader
          num="04"
          tag="§ 4 — ABONNEMENTSVILKÅR"
          title="Abonnementsvilkår"
          color="#8B5CF6"
        />

        <BodyText>
          NordPass tilbyr tre abonnementsnivåer. Alle priser er oppgitt i norske kroner (NOK) inklusiv merverdiavgift (MVA) der dette er aktuelt.
        </BodyText>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          {[
            {
              plan: 'NordPass Gratis',
              price: '0 kr/mnd',
              features: ['Grunnleggende bomkalkulator', 'Begrenset veikart', '1 kjøretøy', 'Standard support'],
              color: '#8A95A3'
            },
            {
              plan: 'NordPass Normal',
              price: '149 kr/mnd',
              features: ['Full bomkalkulator', 'Sanntids veikart', 'Opptil 3 kjøretøy', 'E-post support', 'Rutehistorikk'],
              color: '#3B6FD4'
            },
            {
              plan: 'NordPass Premium',
              price: '299 kr/mnd',
              features: ['Alt i Normal', 'Ubegrenset kjøretøy', 'Prioritert support', 'API-tilgang', 'Avansert analyse', 'Dedikert kontaktperson'],
              color: '#F97316'
            },
          ]?.map((plan, i) => (
            <div
              key={i}
              style={{
                background: `${plan?.color}08`,
                border: `2px solid ${plan?.color}25`,
                borderTop: `4px solid ${plan?.color}`,
                padding: '24px'
              }}
            >
              <div
                style={{
                  fontFamily: 'Impact, sans-serif',
                  fontSize: '1rem',
                  color: plan?.color,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  marginBottom: '4px'
                }}
              >
                {plan?.plan}
              </div>
              <div
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '1.25rem',
                  color: '#EEF0F2',
                  fontWeight: 700,
                  marginBottom: '16px'
                }}
              >
                {plan?.price}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {plan?.features?.map((f, j) => (
                  <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '4px', height: '4px', background: plan?.color, borderRadius: '50%', flexShrink: 0 }} />
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: 'rgba(238,240,242,0.65)' }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            background: 'rgba(139,92,246,0.06)',
            border: '1px solid rgba(139,92,246,0.2)',
            borderLeft: '4px solid #8B5CF6',
            padding: '24px',
            marginBottom: '24px',
            transform: 'rotate(-0.2deg)'
          }}
        >
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.75rem', fontWeight: 700, color: '#8B5CF6', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Kansellering & Refusjon</div>
          <p style={{ fontFamily: 'Georgia, serif', fontSize: '0.9375rem', color: 'rgba(238,240,242,0.72)', lineHeight: 1.75, margin: 0 }}>
            Du kan kansellere abonnementet ditt når som helst via Min konto. Kanselleringen trer i kraft ved slutten av gjeldende faktureringsperiode. NordPass tilbyr ikke refusjon for påbegynte faktureringsperioder, med mindre annet er påkrevd av norsk forbrukerlovgivning (Angrerettloven).{' '}
            <a
              href="/privacy-policy-personvern#rettigheter"
              style={{ color: '#3B6FD4', textDecoration: 'underline', textDecorationColor: 'rgba(59,111,212,0.4)' }}
            >
              Se Personvernerklæringen for dine rettigheter til datasletting →
            </a>
          </p>
        </div>

        <BodyText>
          Abonnementet fornyes automatisk ved slutten av hver faktureringsperiode med mindre det kanselleres. Du vil motta en e-postvarsling 7 dager før fornyelse.
        </BodyText>
      </section>
    </>
  );
};

export default AccountSubscriptionSection;
